import React, { useState } from "react";
import { Modal, Button, Form, Row, Col, Spinner } from "react-bootstrap";
import Select from "react-select";
import { useCreateChamp } from "../../../hooks/CHAMPS/useChampsActions";
import { useGetFieldTypes, useGetFieldApplyStatuses } from "../../../hooks/CHAMPS/useChampMetadata";
import { useGetChamps } from "../../../hooks/CHAMPS/useGetChamps";
import { useClients } from "../../../hooks/useGetClients";
import { customSelectStyles } from "../../../utils/reactSelectStyles";
import { createFieldOption } from "../../../api/CHAMPS/champs";
import { toast } from "react-toastify";

const NewChamp = ({ show, setShow }) => {
  const createChampMutation = useCreateChamp();
  const { data: fieldTypesData, isLoading: typesLoading } = useGetFieldTypes();
  const { data: statusesData, isLoading: statusesLoading } = useGetFieldApplyStatuses();
  const { data: champsData } = useGetChamps();
  const { data: clientsData, isLoading: clientsLoading } = useClients(1, 20, "");

  const [formData, setFormData] = useState({
    label: "",
    nom_colonne: "",
    position: "",
    largeur: "",
    parent: null,
    regle_visibilite: "",
    valeur_defaut: "",
    placeholder: "",
    est_requis: false,
    est_editable: true,
    tous_clients: true,
    statut_application: "",
    type_champ: "",
  });

  const [selectedClients, setSelectedClients] = useState([]);
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState({ value: "", label: "" });

  const handleClose = () => {
    setShow(false);
    resetForm();
  };

  const resetForm = () => {
    setFormData({
      label: "",
      nom_colonne: "",
      position: "",
      largeur: "",
      parent: null,
      regle_visibilite: "",
      valeur_defaut: "",
      placeholder: "",
      est_requis: false,
      est_editable: true,
      tous_clients: true,
      statut_application: "",
      type_champ: "",
    });
    setSelectedClients([]);
    setOptions([]);
    setNewOption({ value: "", label: "" });
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  const handleAddOption = () => {
    if (newOption.value && newOption.label) {
      setOptions([...options, { ...newOption }]);
      setNewOption({ value: "", label: "" });
    }
  };

  const handleRemoveOption = (index) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.label || !formData.nom_colonne || !formData.type_champ || !formData.statut_application) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    const champData = {
      label: formData.label,
      nom_colonne: formData.nom_colonne,
      position: formData.position,
      largeur: formData.largeur,
      parentFieldId: formData.parent || null,
      regle_visibilite: formData.regle_visibilite || null,
      valeur_defaut: formData.valeur_defaut || "",
      placeholder: formData.placeholder || "",
      est_requis: formData.est_requis,
      est_editable: formData.est_editable,
      tous_clients: formData.tous_clients,
      statut_application: formData.statut_application,
      type_champ: parseInt(formData.type_champ),
      options: [],
      clients: formData.tous_clients ? [] : selectedClients.map(c => c.value),
    };

    createChampMutation.mutate(champData, {
      onSuccess: async (response) => {
        const champId = response?.data?.id;
        if (champId && options.length > 0) {
          try {
            await Promise.all(options.map(opt => createFieldOption(champId, opt)));
          } catch {
            toast.warning("Champ créé mais certaines options n'ont pas pu être ajoutées");
            handleClose();
            return;
          }
        }
        toast.success("Champ créé avec succès");
        handleClose();
      },
      onError: (error) => {
        toast.error(error.message || "Erreur lors de la création du champ");
      },
    });
  };

  const fieldTypes = fieldTypesData?.data || [];
  const statuses = statusesData?.data || [];
  const clients = clientsData?.data?.clients || [];
  const champs = champsData?.data || [];

  const clientOptions = clients.map(client => ({
    value: client.id,
    label: client.name,
  }));

  const parentOptions = champs.map(champ => ({
    value: champ.id,
    label: champ.label,
  }));

  const isLoading = typesLoading || statusesLoading || clientsLoading;

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Nouveau champ personnalisé</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center py-5">
            <Spinner animation="border" style={{ color: "#f6c23e" }} />
          </div>
        ) : (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Label <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="label"
                    value={formData.label}
                    onChange={handleChange}
                    placeholder="Ex: Numéro de série"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Nom de colonne <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    type="text"
                    name="nom_colonne"
                    value={formData.nom_colonne}
                    onChange={handleChange}
                    placeholder="Ex: serial_number"
                    required
                  />
                  <Form.Text className="text-muted">
                    Identifiant technique (snake_case)
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Type de champ <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="type_champ"
                    value={formData.type_champ}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez un type</option>
                    {fieldTypes.map(type => (
                      <option key={type.id} value={type.id}>
                        {type.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Statut d'application <span className="text-danger">*</span></Form.Label>
                  <Form.Select
                    name="statut_application"
                    value={formData.statut_application}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Sélectionnez un statut</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>
                        {status.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>Champ parent</Form.Label>
                  <Select
                    isClearable
                    value={parentOptions.find(o => o.value === formData.parent) ?? null}
                    onChange={(opt) => setFormData(prev => ({ ...prev, parent: opt ? opt.value : null }))}
                    options={parentOptions}
                    placeholder="Aucun parent (champ racine)"
                    noOptionsMessage={() => "Aucun champ disponible"}
                    styles={customSelectStyles}
                  />
                  <Form.Text className="text-muted">
                    Laisser vide pour un champ racine
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Position</Form.Label>
                  <Form.Control
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleChange}
                    placeholder="1"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Largeur</Form.Label>
                  <Form.Control
                    type="text"
                    name="largeur"
                    value={formData.largeur}
                    onChange={handleChange}
                    placeholder="50%"
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Valeur par défaut</Form.Label>
                  <Form.Control
                    type="text"
                    name="valeur_defaut"
                    value={formData.valeur_defaut}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Placeholder</Form.Label>
                  <Form.Control
                    type="text"
                    name="placeholder"
                    value={formData.placeholder}
                    onChange={handleChange}
                    placeholder="Texte d'aide"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Règle de visibilité</Form.Label>
                  <Form.Control
                    type="text"
                    name="regle_visibilite"
                    value={formData.regle_visibilite}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="est_requis"
                    label="Requis"
                    checked={formData.est_requis}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="est_editable"
                    label="Éditable"
                    checked={formData.est_editable}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="tous_clients"
                    label="Disponible pour tous les clients"
                    checked={formData.tous_clients}
                    onChange={handleChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            {!formData.tous_clients && (
              <Form.Group className="mb-3">
                <Form.Label>Clients associés</Form.Label>
                <Select
                  isMulti
                  value={selectedClients}
                  onChange={setSelectedClients}
                  options={clientOptions}
                  placeholder="Sélectionnez les clients"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  styles={customSelectStyles}
                />
              </Form.Group>
            )}

            {/* Options pour select */}
            {fieldTypes.find(t => t.id === parseInt(formData.type_champ))?.name === 'Select' && (
              <div className="mb-3">
                <Form.Label>Options du champ</Form.Label>
                <div className="border rounded p-3 bg-light">
                  {options.map((opt, index) => (
                    <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                      <code className="flex-grow-1">{opt.value}</code>
                      <span className="flex-grow-1">{opt.label}</span>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemoveOption(index)}
                      >
                        Retirer
                      </Button>
                    </div>
                  ))}
                  <Row>
                    <Col md={5}>
                      <Form.Control
                        type="text"
                        value={newOption.value}
                        onChange={(e) => setNewOption({ ...newOption, value: e.target.value })}
                        placeholder="Valeur"
                        size="sm"
                      />
                    </Col>
                    <Col md={5}>
                      <Form.Control
                        type="text"
                        value={newOption.label}
                        onChange={(e) => setNewOption({ ...newOption, label: e.target.value })}
                        placeholder="Label"
                        size="sm"
                      />
                    </Col>
                    <Col md={2}>
                      <Button
                        variant="success"
                        size="sm"
                        onClick={handleAddOption}
                        disabled={!newOption.value || !newOption.label}
                      >
                        Ajouter
                      </Button>
                    </Col>
                  </Row>
                </div>
              </div>
            )}
          </Form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Annuler
        </Button>
        <Button
          variant="warning"
          onClick={handleSubmit}
          disabled={createChampMutation.isPending || isLoading}
        >
          {createChampMutation.isPending ? (
            <>
              Création...
            </>
          ) : (
            "Créer le champ"
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NewChamp;
